import React, { useState } from 'react';
import {
  Checkbox,
  IconButton,
  Input,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/**
 * Format bytes to human-readable size
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Format date to locale string
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Get icon for file type based on name/extension
 */
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase();

  const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
  const pdfExts = ['pdf'];
  const docExts = ['doc', 'docx', 'txt', 'rtf'];
  const spreadsheetExts = ['xls', 'xlsx', 'csv'];
  const jsonExts = ['json'];

  if (imageExts.includes(ext)) return <ImageIcon sx={{ color: 'primary.500' }} />;
  if (pdfExts.includes(ext)) return <PictureAsPdfIcon sx={{ color: '#e53935' }} />;
  if (docExts.includes(ext)) return <DescriptionIcon sx={{ color: '#1976d2' }} />;
  if (spreadsheetExts.includes(ext)) return <TableChartIcon sx={{ color: '#388e3c' }} />;
  if (jsonExts.includes(ext)) return <DataObjectIcon sx={{ color: '#f57c00' }} />;

  return <InsertDriveFileOutlinedIcon sx={{ color: 'neutral.500' }} />;
};

const FileRow = ({
  item,
  isSelected,
  onSelect,
  onOpen,
  onDownload,
  onContextMenu,
  isRenaming,
  onRenameSubmit,
  onRenameCancel,
  isDragging,
  isDropTarget,
  dragHandleProps,
  draggableProps,
  innerRef,
}) => {
  const [hovered, setHovered] = useState(false);
  const [renameValue, setRenameValue] = useState(item.name);
  const isFolder = item.type === 'folder';

  const handleDoubleClick = (e) => {
    if (!isRenaming) {
      e.stopPropagation();
      onOpen(item);
    }
  };

  const handleClick = (e) => {
    if (!isRenaming) {
      onSelect(item.path, e);
    }
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onRenameSubmit(renameValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onRenameCancel();
    }
  };

  const handleMoreClick = (e) => {
    e.stopPropagation();
    onContextMenu(e, item);
  };

  return (
    <tr
      ref={innerRef}
      {...draggableProps}
      {...dragHandleProps}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={(e) => onContextMenu(e, item)}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        backgroundColor: isSelected
          ? 'var(--joy-palette-primary-50)'
          : isDropTarget
          ? 'var(--joy-palette-primary-100)'
          : isDragging
          ? 'var(--joy-palette-background-level2)'
          : hovered
          ? 'var(--joy-palette-background-level1)'
          : 'transparent',
        boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
        opacity: isDragging ? 0.9 : 1,
        borderLeft: isDropTarget
          ? '3px solid var(--joy-palette-primary-500)'
          : '3px solid transparent',
        transition: 'background-color 0.1s, border-color 0.1s',
        ...draggableProps?.style,
      }}
    >
      {/* Checkbox */}
      <td style={{ width: 48, textAlign: 'center' }}>
        <Checkbox
          size="sm"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(item.path, { ctrlKey: true });
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            opacity: hovered || isSelected ? 1 : 0.3,
            transition: 'opacity 0.1s',
          }}
        />
      </td>

      {/* Name */}
      <td>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {isFolder ? (
            <FolderIcon sx={{ color: '#ffc107', fontSize: 24 }} />
          ) : (
            getFileIcon(item.name)
          )}
          {isRenaming ? (
            <Input
              size="sm"
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={() => onRenameCancel()}
              onClick={(e) => e.stopPropagation()}
              sx={{
                flex: 1,
                maxWidth: 400,
                '--Input-focusedThickness': '1px',
              }}
            />
          ) : (
            <Typography
              level="body-sm"
              fontWeight={isFolder ? 500 : 400}
              sx={{
                color: isFolder ? 'primary.600' : 'text.primary',
                '&:hover': isFolder
                  ? { textDecoration: 'underline' }
                  : undefined,
              }}
            >
              {item.name}
            </Typography>
          )}
        </Stack>
      </td>

      {/* Size */}
      <td>
        <Typography level="body-sm" color="neutral">
          {isFolder ? '-' : formatFileSize(item.size || 0)}
        </Typography>
      </td>

      {/* Modified */}
      <td>
        <Typography level="body-sm" color="neutral">
          {isFolder ? '-' : formatDate(item.last_modified)}
        </Typography>
      </td>

      {/* Actions */}
      <td>
        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={0.5}
          sx={{
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.1s',
          }}
        >
          {!isFolder && (
            <Tooltip title="Download">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(item.path, item.name);
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="More actions">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={handleMoreClick}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </td>
    </tr>
  );
};

export default FileRow;
